/**
 * The interface layer (decision 0013, `docs/design/ui.md`): the HUD, the start sheet, the result
 * sheet, the menu and its pages, field notes, the hint box and the sealed dialog, in plain DOM
 * over the world, restyled by the theme. It holds no game state: main.ts tells it what to show
 * and it reports what the player chose.
 */
import type { ThemeDef } from "@mistsweeper/assets";
import { NAME_MAX } from "./clearings.ts";
import { SHOW_BETA, SHOW_GET_APP } from "./features.ts";
import { isLocale, type Locale, type StringKey, t as translate } from "./i18n.ts";
import { HOLD_MS } from "./touch.ts";
import { bind, esc, fragment, icon, press, SQUARE, wheelSideways } from "./ui/dom.ts";
import { type FindNote, FindNotes } from "./ui/finds.ts";
import { hudHtml } from "./ui/hud.ts";
import {
  type MenuData,
  type MenuPage,
  menuShell,
  miniMap,
  pageHtml,
  pageTitle,
} from "./ui/menu.ts";
import { type ResultContent, type ResultListRow, resultHtml, splitShare } from "./ui/result.ts";
import { type StartState, startHtml, startTexts } from "./ui/start.ts";

export type Screen = "start" | "play" | "end";
export type Pick = "daily" | "endless";
/** Where the player is: on an expedition, or in the clearing (decision 0015). */
export type Place = "run" | "clearing";
/** The clearing's size, as the menu of a held clearing in the row names it. */
export type Reach = "small" | "large";
/** The clearing arranged, or looked at through the photo mode's viewfinder. */
export type ClearingMode = "arrange" | "photo";
/** A clearing as the row under the top bar shows it. */
export interface ClearingName {
  readonly id: number;
  readonly name: string;
}

export interface UiHandlers {
  /** The HUD's Today and Endless. */
  pickMode(mode: Pick): void;
  /** Try it in the world, on How to play: the lesson (decision 0045). */
  lesson(): void;
  banner(): void;
  cabin(): void;
  north(): void;
  /** The clearing in the top bar: the clearing, in place, as Today and Endless. */
  clearing(): void;
  /** In the clearing: Arrange and Photo in the bottom bar, and the clearing's size. */
  clearingMode(mode: ClearingMode): void;
  reach(size: Reach): void;
  /** The row of clearings: one chosen, a new one made, and the one showing renamed or deleted. */
  pickClearing(id: number): void;
  addClearing(): void;
  renameClearing(name: string): void;
  deleteClearing(): void;
  turn(step: number): void;
  zoom(step: number): void;
  /** The start sheet's button, for the option chosen. */
  setOut(pick: Pick): void;
  /** The result sheet's buttons. */
  share(): void;
  again(): void;
  practice(): void;
  startNext(): void;
  setTheme(theme: "day" | "night"): void;
  /** On, the theme follows the system's light or dark mode; off, the one shown stays. */
  setThemeAuto(on: boolean): void;
  /** A language, or null to follow the browser's again. */
  setLocale(locale: Locale | null): void;
  setReduceMotion(on: boolean): void;
  setMusic(on: boolean): void;
  setSounds(on: boolean): void;
  setFrameTimes(on: boolean): void;
  /** Tilt-shift, a developer trial: from 0, off, to 1. */
  setTilt(amount: number): void;
  /** The banner for the visit (decision 0035): `b2`, or `1` for the game's own. */
  setBanner(variant: string): void;
  /** A row or button that leaves the game. */
  external(kind: "store"): void;
  /** Send feedback: a new email to Great Scott!. */
  feedback(): void;
  /** Opens a page outside the game, such as an app in the App Store. */
  openUrl(url: string): void;
  /** The data the menu pages show, read when a page opens. */
  menuData(): MenuData;
  /** The menu opened or closed, or turned a page while open. */
  menuChanged(open: boolean): void;
  /** A card for a new find came out under the clearing's cell. */
  findShown(): void;
  /** Today's expeditions (decision 0039): the page opened, so the list is fetched. */
  listShown(): void;
  /** Add, on the result sheet or the list's own row: with the name kept, or the dialog asks. */
  listAdd(): void;
  /** The name dialog's button: the first expedition added under it, or the name changed. */
  listName(mode: "add" | "rename", name: string): void;
  setListAuto(on: boolean): void;
  /** Remove my expeditions, once the question is answered. */
  listRemove(): void;
  /** A held row's name reported, once the question is answered. */
  listReport(expedition: number): void;
}

export interface HudState {
  readonly mode: Pick;
  readonly distance: string;
  readonly modeLine: string;
  /** Under Today: "#12". */
  readonly dailyShort: string;
  /** Under Endless: "Best 1.6 km", and "Best 0 m" before the first run. */
  readonly endlessKicker: string;
}

const NOTE_MS = 1800;
/** How long after a hold opened a clearing's menu the click of the lifted finger is let go. */
const HELD_CLICK_MS = 700;
/** How long the menu takes to slide out before it is hidden; matches the style sheet. */
const MENU_OUT_MS = 220;
/** Long enough for the last row of a page to fade in (12 steps of 35 ms and 280 ms). */
const CASCADE_MS = 760;

export class Ui {
  readonly root: HTMLElement;
  private locale: Locale;
  private readonly h: UiHandlers;
  private readonly web = true;
  private readonly getApp = SHOW_GET_APP;
  private readonly beta = SHOW_BETA;
  private finds!: FindNotes;
  /** What has been found and how much there is, for the clearing's cell; kept across rebuilds. */
  private found: readonly [number, number] | null = null;
  private place: Place = "run";
  /** The clearing's size and whether its photo mode is open, as the bars show them. */
  private tools: { reach: Reach; photo: boolean } = { reach: "small", photo: false };
  /** The player's clearings in the row under the top bar, and the one that shows. */
  private clearings: readonly ClearingName[] = [];
  private chosenClearing = 0;
  /** When a held clearing last opened its menu, so the click its lifted finger makes does nothing. */
  private heldAt = Number.NEGATIVE_INFINITY;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private clearingsMenu!: HTMLElement;
  private renameDialog!: HTMLElement;
  private deleteDialog!: HTMLElement;
  /** Today's expeditions: the name asked for, and a question before a removal or a report. */
  private listNameDialog!: HTMLElement;
  private listConfirmDialog!: HTMLElement;
  private listNameMode: "add" | "rename" = "add";
  private confirmed: (() => void) | null = null;
  private reportTimer: ReturnType<typeof setTimeout> | null = null;
  private listRow: ResultListRow | null = null;
  private readonly deskQuery = matchMedia(
    "(min-width: 900px) and (hover: hover) and (pointer: fine)",
  );
  private layer!: HTMLElement;
  private hud!: HTMLElement;
  private start!: HTMLElement;
  private result!: HTMLElement;
  private menu!: HTMLElement;
  private hint!: HTMLElement;
  private note!: HTMLElement;
  private sealed!: HTMLElement;
  private debug!: HTMLElement;
  private screen: Screen = "play";
  private hudState: HudState | null = null;
  private startState: StartState | null = null;
  private resultContent: ResultContent | null = null;
  private pick: Pick = "daily";
  private bannerOn = false;
  private compassDegrees = 0;
  private hintKey: StringKey | null = null;
  private page: MenuPage = "home";
  private menuReturn: HTMLElement | null = null;
  private menuTimer: ReturnType<typeof setTimeout> | undefined;
  private cascadeTimer: ReturnType<typeof setTimeout> | undefined;
  private noteTimer: ReturnType<typeof setTimeout> | null = null;
  private shareLabel: StringKey = "over.share";

  constructor(root: HTMLElement, locale: Locale, handlers: UiHandlers) {
    this.root = root;
    this.locale = locale;
    this.h = handlers;
    this.build();
    this.deskQuery.addEventListener("change", () => this.refreshLayout());
    addEventListener("resize", () => {
      this.fitClearings();
      this.fitFurthest();
    });
    // A press anywhere but the menu of a held clearing, the world's too, puts the menu away.
    document.addEventListener(
      "pointerdown",
      (e) => {
        if (!(e.target as Element).closest?.(".clearings-menu")) this.closeClearingMenu();
      },
      { capture: true, passive: true },
    );
  }

  private t = (key: StringKey, params: Record<string, string | number> = {}) =>
    translate(this.locale, key, params);

  get isDesk(): boolean {
    return this.deskQuery.matches;
  }

  /** The interface layer itself, for the lesson's card and marks (decision 0045). */
  get element(): HTMLElement {
    return this.layer;
  }

  /** Builds every part in the current language; called again when the language changes. */
  private build(): void {
    this.layer?.remove();
    const layer = fragment('<div id="ui"></div>');
    this.hud = fragment(hudHtml(this.t, this.web && this.getApp));
    this.start = fragment(startHtml(this.t, this.web && this.getApp, this.web && this.beta));
    this.result = fragment(resultHtml(this.t));
    this.menu = fragment(menuShell(this.t, this.web && this.beta));
    this.hint = fragment('<div class="hint" role="status" hidden></div>');
    this.note = fragment('<div class="field-note" role="status" aria-live="polite" hidden></div>');
    this.sealed = fragment(`
      <div class="dialog-layer" hidden>
        <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="sealedTitle">
          <div class="headline">${SQUARE}<h2 id="sealedTitle" data-bind="sealedTitle"></h2></div>
          <p data-bind="sealedBody"></p>
          <div class="dialog-buttons">
            <button type="button" class="primary small" data-action="startNext" data-bind="sealedNext"></button>
            <button type="button" class="secondary" data-action="closeDialog">${esc(this.t("button.close"))}</button>
          </div>
        </div>
      </div>`);
    // A clearing's new name, and the question before one is deleted (the row of clearings).
    this.renameDialog = fragment(`
      <div class="dialog-layer" hidden>
        <form class="dialog" role="dialog" aria-modal="true" aria-labelledby="renameTitle" data-form="rename">
          <div class="headline">${SQUARE}<h2 id="renameTitle">${esc(this.t("clearing.renameTitle"))}</h2></div>
          <input class="dialog-input" type="text" name="name" maxlength="${NAME_MAX}" autocomplete="off" autocapitalize="words" spellcheck="false" enterkeyhint="done" aria-labelledby="renameTitle">
          <div class="dialog-buttons">
            <button type="submit" class="primary small">${esc(this.t("clearing.renameSave"))}</button>
            <button type="button" class="secondary" data-action="closeDialog">${esc(this.t("button.cancel"))}</button>
          </div>
        </form>
      </div>`);
    this.deleteDialog = fragment(`
      <div class="dialog-layer" hidden>
        <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="deleteTitle">
          <div class="headline">${SQUARE}<h2 id="deleteTitle" data-bind="deleteTitle"></h2></div>
          <p>${esc(this.t("clearing.deleteBody"))}</p>
          <div class="dialog-buttons">
            <button type="button" class="primary small" data-action="confirmDelete">${esc(this.t("clearing.delete"))}</button>
            <button type="button" class="secondary" data-action="closeDialog">${esc(this.t("button.cancel"))}</button>
          </div>
        </div>
      </div>`);
    // Today's expeditions (decision 0039): the name, when the first expedition is added or the
    // name changes, and the question before the expeditions leave the lists or a name is reported.
    this.listNameDialog = fragment(`
      <div class="dialog-layer" hidden>
        <form class="dialog" role="dialog" aria-modal="true" aria-labelledby="listNameTitle" data-form="listName" novalidate>
          <div class="headline">${SQUARE}<h2 id="listNameTitle" data-bind="listNameTitle"></h2></div>
          <p data-bind="listNameBody"></p>
          <input class="dialog-input" type="text" name="name" maxlength="40" autocomplete="nickname" autocapitalize="words" spellcheck="false" enterkeyhint="done" aria-label="${esc(this.t("list.nameLabel"))}" placeholder="${esc(this.t("list.nameLabel"))}">
          <p class="dialog-error" role="alert" data-bind="listNameError"></p>
          <div class="dialog-buttons">
            <button type="submit" class="primary small" data-bind="listNameSubmit"></button>
            <button type="button" class="secondary" data-action="closeDialog">${esc(this.t("button.cancel"))}</button>
          </div>
        </form>
      </div>`);
    this.listConfirmDialog = fragment(`
      <div class="dialog-layer" hidden>
        <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="listConfirmTitle">
          <div class="headline">${SQUARE}<h2 id="listConfirmTitle" data-bind="listConfirmTitle"></h2></div>
          <p data-bind="listConfirmBody"></p>
          <div class="dialog-buttons">
            <button type="button" class="primary small" data-action="listConfirm" data-bind="listConfirmButton"></button>
            <button type="button" class="secondary" data-action="closeDialog">${esc(this.t("button.cancel"))}</button>
          </div>
        </div>
      </div>`);
    this.debug = fragment('<pre id="debug" hidden></pre>');
    if (this.layer?.dataset.motion) layer.dataset.motion = this.layer.dataset.motion;
    if (this.layer?.classList.contains("world-only")) layer.classList.add("world-only");
    layer.classList.toggle("in-clearing", this.place === "clearing");
    this.finds = new FindNotes(
      this.hud,
      this.t,
      () => this.layer.dataset.motion === "reduce",
      () => this.h.findShown(),
    );
    if (this.found) this.finds.setFound(...this.found);
    layer.append(
      this.hud,
      this.hint,
      this.start,
      this.result,
      this.note,
      this.menu,
      this.sealed,
      this.renameDialog,
      this.deleteDialog,
      this.listNameDialog,
      this.listConfirmDialog,
      this.debug,
    );
    layer.addEventListener("click", (e) => this.onClick(e));
    layer.addEventListener("submit", (e) => {
      const form = e.target as HTMLFormElement;
      const name = (form.elements.namedItem("name") as HTMLInputElement | null)?.value ?? "";
      if (form.dataset.form === "listName") {
        e.preventDefault();
        this.h.listName(this.listNameMode, name);
        return;
      }
      if (form.dataset.form !== "rename") return;
      e.preventDefault();
      this.closeDialogs();
      this.h.renameClearing(name);
    });
    this.wireReports(this.menu);
    // Escape closes a dialog from its text field too, which the world's keys leave alone.
    layer.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !this.anyDialog) return;
      e.preventDefault();
      e.stopPropagation();
      this.closeDialogs();
    });
    this.clearingsMenu = this.hud.querySelector(".clearings-menu") as HTMLElement;
    this.wireClearings(this.hud.querySelector(".clearings-row") as HTMLElement);
    this.renderClearings();
    layer.addEventListener("input", (e) => {
      const input = e.target as HTMLInputElement;
      if (input.dataset.input === "tilt") this.h.setTilt(Number(input.value) / 100);
      if (input.dataset.input === "banner") this.h.setBanner(input.value);
    });
    this.root.append(layer);
    this.layer = layer;
    this.refreshLayout();
  }

  setLocale(locale: Locale): void {
    this.locale = locale;
    const menuOpen = this.menuOpen;
    this.build();
    if (menuOpen) this.openMenu(this.page, null, false);
  }

  /** Re-applies everything that depends on the language or the layout. */
  private refreshLayout(): void {
    const desk = this.isDesk;
    this.layer.classList.toggle("is-desk", desk);
    this.layer.classList.toggle("is-phone", !desk);
    this.layer.dataset.screen = this.screen;
    this.applyPlace();
    this.setBannerMode(this.bannerOn);
    this.setCompass(this.compassDegrees, false);
    this.applyHint();
    if (this.startState) this.applyStart();
    if (this.resultContent) this.applyResult(this.resultContent);
    if (this.menuOpen) this.renderPage();
  }

  private onClick(e: MouseEvent): void {
    const target = (e.target as Element).closest<HTMLElement>("[data-action]");
    if (!target) return;
    const action = target.dataset.action ?? "";
    const [verb, arg] = action.split(":");
    switch (verb) {
      case "menu":
        this.openMenu("home", target);
        break;
      case "closeMenu":
        this.closeMenu();
        break;
      case "menuBack":
        this.showPage(this.page === "language" ? "settings" : "home");
        break;
      case "page":
        this.showPage(arg as MenuPage);
        break;
      case "howto":
        this.openMenu("howto", target);
        break;
      case "lesson":
        this.closeMenu();
        this.h.lesson();
        break;
      case "getApp":
        this.openMenu("getapp", target);
        break;
      case "beta":
        this.openMenu("beta", target);
        break;
      case "daily":
        this.h.pickMode("daily");
        break;
      case "endless":
        this.h.pickMode("endless");
        break;
      case "banner":
        this.h.banner();
        break;
      case "cabin":
        this.h.cabin();
        break;
      case "north":
        this.h.north();
        break;
      case "clearing":
        this.h.clearing();
        break;
      case "arrange":
        this.h.clearingMode("arrange");
        break;
      case "photo":
        this.h.clearingMode("photo");
        break;
      case "reach":
        this.h.reach(arg === "large" ? "large" : "small");
        break;
      case "pickClearing":
        // The click that the finger of a hold makes as it lifts only leaves the menu showing.
        if (performance.now() - this.heldAt > HELD_CLICK_MS) this.h.pickClearing(Number(arg));
        break;
      case "addClearing":
        this.h.addClearing();
        break;
      case "renameClearing":
        this.openRename();
        break;
      case "deleteClearing":
        this.openDelete();
        break;
      case "confirmDelete":
        this.closeDialogs();
        this.h.deleteClearing();
        break;
      case "turnLeft":
        this.h.turn(-1);
        break;
      case "turnRight":
        this.h.turn(1);
        break;
      case "zoomIn":
        this.h.zoom(1);
        break;
      case "zoomOut":
        this.h.zoom(-1);
        break;
      case "pickDaily":
        this.pick = "daily";
        this.applyStart();
        break;
      case "pickEndless":
        this.pick = "endless";
        this.applyStart();
        break;
      case "setOut":
        this.h.setOut(this.pick);
        break;
      case "result":
        this.onResult(arg ?? "");
        break;
      case "startNext":
        this.h.startNext();
        break;
      case "closeDialog":
        this.closeDialogs();
        break;
      case "listOpen":
        this.openMenu("list", target);
        break;
      case "listAdd":
        this.h.listAdd();
        break;
      case "listRename":
        this.openListName("rename", target.dataset.name ?? "");
        break;
      case "listAuto":
        this.h.setListAuto(target.getAttribute("aria-checked") !== "true");
        this.renderPage();
        break;
      case "listRemove":
        this.openConfirm("list.removeTitle", "list.removeBody", "list.remove", () =>
          this.h.listRemove(),
        );
        break;
      case "listConfirm": {
        const confirmed = this.confirmed;
        this.closeDialogs();
        confirmed?.();
        break;
      }
      case "theme":
        this.h.setTheme(arg === "night" ? "night" : "day");
        this.renderPage();
        break;
      case "themeAuto":
        this.h.setThemeAuto(target.getAttribute("aria-checked") !== "true");
        this.renderPage();
        break;
      case "locale":
        this.h.setLocale(isLocale(arg) ? arg : null);
        break;
      case "motion":
        this.h.setReduceMotion(target.getAttribute("aria-checked") !== "true");
        this.renderPage();
        break;
      case "music":
        this.h.setMusic(target.getAttribute("aria-checked") !== "true");
        this.renderPage();
        break;
      case "sounds":
        this.h.setSounds(target.getAttribute("aria-checked") !== "true");
        this.renderPage();
        break;
      case "frameTimes":
        this.h.setFrameTimes(target.getAttribute("aria-checked") !== "true");
        this.renderPage();
        break;
      case "store":
        this.h.external(verb);
        break;
      case "feedback":
        this.h.feedback();
        break;
      case "open":
        if (target.dataset.url) this.h.openUrl(target.dataset.url);
        break;
    }
    // Whatever was chosen in a held clearing's menu, the menu has done its work.
    if (target.closest(".clearings-menu")) this.closeClearingMenu();
  }

  // The screens.

  setScreen(screen: Screen): void {
    this.screen = screen;
    this.layer.dataset.screen = screen;
    this.result.classList.toggle("shown", screen === "end");
    this.applyHint();
  }

  get currentScreen(): Screen {
    return this.screen;
  }

  // The HUD.

  setHud(state: HudState): void {
    this.hudState = state;
    const r = this.hud;
    bind(r, "distance", state.distance);
    bind(r, "mode", state.modeLine);
    bind(r, "dailyShort", state.dailyShort);
    bind(r, "endlessKicker", state.endlessKicker);
    // In the clearing Today and Endless wait unpressed, and the hint line speaks of the clearing.
    const run = this.place === "run";
    const hint: StringKey = run
      ? "hint.revealDesktop"
      : this.tools.photo
        ? "hint.photoDesktop"
        : "hint.clearingDesktop";
    bind(r, "deskHint", this.t(hint));
    press(r, "daily", run && state.mode === "daily");
    press(r, "endless", run && state.mode === "endless");
  }

  /**
   * The expedition or the clearing: the clearing pressed in the top bar as Today or
   * Endless is, and the bottom bar holds the clearing's Arrange and Photo in place of the
   * expedition's distance and banner.
   */
  setPlace(place: Place): void {
    this.place = place;
    this.applyPlace();
  }

  /** The clearing's size and whether its photo mode is open, in the bars. */
  setClearingTools(reach: Reach, photo: boolean): void {
    this.tools = { reach, photo };
    this.applyPlace();
  }

  /** The player's clearings for the row under the top bar, and the one that shows. */
  setClearings(clearings: readonly ClearingName[], chosen: number): void {
    this.clearings = clearings;
    this.chosenClearing = chosen;
    this.renderClearings();
  }

  /**
   * The row's cells: built anew when a clearing comes, goes or is renamed, and otherwise only
   * pressed, so a row scrolled sideways stays where it was. The plus comes last, inside the row,
   * where its sticky place keeps it at the end once the clearings run past it.
   */
  private renderClearings(): void {
    const row = this.hud.querySelector<HTMLElement>(".clearings-row");
    if (!row) return;
    const cells = [...row.querySelectorAll<HTMLElement>("[data-clearing]")];
    const same =
      cells.length === this.clearings.length &&
      cells.every((c, i) => {
        const named = this.clearings[i];
        return named && c.dataset.clearing === String(named.id) && c.textContent === named.name;
      });
    if (!same) {
      row.innerHTML = `${this.clearings
        .map(
          (c) =>
            `<button type="button" class="clearings-cell" data-clearing="${c.id}" data-action="pickClearing:${c.id}" aria-pressed="false">${esc(c.name)}</button>`,
        )
        .join(
          "",
        )}<button type="button" class="clearings-add" data-action="addClearing" aria-label="${esc(this.t("clearing.new"))}">${icon("zoomIn", 22)}</button>`;
    }
    for (const cell of row.querySelectorAll<HTMLElement>("[data-clearing]")) {
      cell.setAttribute(
        "aria-pressed",
        String(cell.dataset.clearing === String(this.chosenClearing)),
      );
    }
    this.fitClearings();
  }

  /**
   * Whether the clearings run past the row, where the plus stands at the screen's edge and drops
   * its outer line; and the one showing scrolled into view.
   */
  private fitClearings(): void {
    const row = this.hud.querySelector<HTMLElement>(".clearings-row");
    if (!row || row.clientWidth === 0) return;
    row.classList.toggle("overflows", row.scrollWidth > row.clientWidth + 1);
    const chosen = row.querySelector<HTMLElement>(`[data-clearing="${this.chosenClearing}"]`);
    const plus = row.querySelector<HTMLElement>(".clearings-add");
    if (!chosen || !plus) return;
    // In view and clear of the plus, which stands over the row's end.
    const left = chosen.offsetLeft;
    const right = left + chosen.offsetWidth + plus.offsetWidth;
    if (left < row.scrollLeft) row.scrollLeft = left;
    else if (right > row.scrollLeft + row.clientWidth) row.scrollLeft = right - row.clientWidth;
  }

  /** A hold or a secondary click on a clearing opens its menu; a sideways swipe scrolls the row. */
  private wireClearings(row: HTMLElement): void {
    let down: { x: number; y: number; id: number } | null = null;
    const cancel = () => {
      if (this.holdTimer !== null) clearTimeout(this.holdTimer);
      this.holdTimer = null;
      down = null;
    };
    const cellOf = (e: Event) =>
      (e.target as Element).closest<HTMLElement>("[data-clearing]") ?? null;
    row.addEventListener("pointerdown", (e) => {
      const cell = cellOf(e);
      cancel();
      if (!cell || e.button !== 0) return;
      const id = Number(cell.dataset.clearing);
      down = { x: e.clientX, y: e.clientY, id };
      this.holdTimer = setTimeout(() => {
        this.holdTimer = null;
        down = null;
        this.holdClearing(id);
      }, HOLD_MS);
    });
    row.addEventListener("pointermove", (e) => {
      if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) > 8) cancel();
    });
    row.addEventListener("pointerup", cancel);
    row.addEventListener("pointercancel", cancel);
    row.addEventListener("scroll", cancel, { passive: true });
    row.addEventListener("contextmenu", (e) => {
      const cell = cellOf(e);
      if (!cell) return;
      e.preventDefault();
      cancel();
      this.holdClearing(Number(cell.dataset.clearing));
    });
    wheelSideways(row);
  }

  /** A held clearing shows, if it did not, and opens its menu under it. */
  private holdClearing(id: number): void {
    this.heldAt = performance.now();
    if (id !== this.chosenClearing) this.h.pickClearing(id);
    this.openClearingMenu(id);
  }

  private openClearingMenu(id: number): void {
    const named = this.clearings.find((c) => c.id === id);
    const cell = this.hud.querySelector<HTMLElement>(`[data-clearing="${id}"]`);
    const menu = this.clearingsMenu;
    if (!named || !cell) return;
    bind(menu, "clearingMenuName", named.name);
    // The last clearing stays: there is always one.
    const del = menu.querySelector<HTMLElement>('[data-action="deleteClearing"]');
    if (del) del.hidden = this.clearings.length <= 1;
    menu.hidden = false;
    const at = cell.getBoundingClientRect();
    const left = Math.min(Math.max(8, at.left), innerWidth - menu.offsetWidth - 8);
    menu.style.left = `${Math.round(left)}px`;
    menu.style.top = `${Math.round(at.bottom + 8)}px`;
  }

  closeClearingMenu(): void {
    if (this.clearingsMenu && !this.clearingsMenu.hidden) this.clearingsMenu.hidden = true;
  }

  /** The new name for the clearing that shows, its old one ready to change. */
  private openRename(): void {
    const named = this.clearings.find((c) => c.id === this.chosenClearing);
    const input = this.renameDialog.querySelector<HTMLInputElement>("input");
    if (!named || !input) return;
    input.value = named.name;
    this.renameDialog.hidden = false;
    input.focus();
    input.select();
  }

  /** Asks before the clearing that shows is deleted, with what was set out in it. */
  private openDelete(): void {
    const named = this.clearings.find((c) => c.id === this.chosenClearing);
    if (!named || this.clearings.length <= 1) return;
    bind(this.deleteDialog, "deleteTitle", this.t("clearing.deleteTitle", { name: named.name }));
    this.deleteDialog.hidden = false;
    this.deleteDialog.querySelector<HTMLElement>('[data-action="closeDialog"]')?.focus();
  }

  private applyPlace(): void {
    const inClearing = this.place === "clearing";
    if (!inClearing || this.tools.photo) this.closeClearingMenu();
    this.layer.classList.toggle("in-clearing", inClearing);
    this.layer.classList.toggle("in-photo", inClearing && this.tools.photo);
    press(this.hud, "clearing", inClearing);
    press(this.hud, "reach:small", this.tools.reach === "small");
    press(this.hud, "reach:large", this.tools.reach === "large");
    press(this.hud, "arrange", !this.tools.photo);
    press(this.hud, "photo", this.tools.photo);
    if (this.hudState) this.setHud(this.hudState);
    this.applyHint();
    // The row had no width while it was hidden.
    if (inClearing) this.fitClearings();
  }

  /** What has been found, in the clearing's cell. */
  setFound(found: number, total: number): void {
    this.found = [found, total];
    this.finds.setFound(found, total);
  }

  /** New finds: a card under the clearing's cell, then its count up to `found`. */
  noteFinds(notes: readonly FindNote[], found: number, total: number): void {
    this.found = [found, total];
    this.finds.note(notes, found, total);
  }

  setBannerMode(on: boolean): void {
    this.bannerOn = on;
    press(this.hud, "banner", on);
    this.applyHint();
  }

  /** The compass's turn in degrees, clockwise; without a transition while the camera is dragged. */
  setCompass(degrees: number, animate: boolean): void {
    this.compassDegrees = degrees;
    for (const svg of this.hud.querySelectorAll<SVGElement>(".compass")) {
      svg.style.transition = animate ? "" : "none";
      svg.style.transform = `rotate(${degrees.toFixed(1)}deg)`;
    }
  }

  // The hint box: what waits on the player. The start hint until the first reveal, and banner mode.

  showStartHint(): void {
    this.hintKey = "hint.reveal";
    this.applyHint();
  }

  hideHint(): void {
    if (this.hintKey === null) return;
    this.hintKey = null;
    this.applyHint();
  }

  private applyHint(): void {
    const playing = this.screen === "play";
    // On desktop the hint lives in the bottom bar instead of a box. Banner mode shows in its
    // button and a field note, not here.
    const key: StringKey | null = this.isDesk ? null : this.hintKey;
    // The clearing has a hint of its own, over its tray.
    const show = playing && key !== null && this.place === "run";
    this.hint.hidden = !show;
    if (show && key) this.hint.textContent = this.t(key);
  }

  /** A field note: confirms an action for 1.8 seconds, replacing the one before. */
  showNote(text: string, ms = NOTE_MS): void {
    if (this.noteTimer !== null) clearTimeout(this.noteTimer);
    this.note.textContent = text;
    this.note.hidden = false;
    this.noteTimer = setTimeout(() => {
      this.note.hidden = true;
      this.noteTimer = null;
    }, ms);
  }

  // The start sheet.

  showStart(state: StartState): void {
    this.startState = state;
    this.pick = state.today === "done" ? "endless" : "daily";
    this.applyStart();
    this.setScreen("start");
  }

  private applyStart(): void {
    if (!this.startState) return;
    const texts = startTexts(this.t, this.startState, this.pick, this.isDesk);
    for (const [k, v] of Object.entries(texts)) bind(this.start, k, v);
    press(this.start, "pickDaily", this.pick === "daily");
    press(this.start, "pickEndless", this.pick === "endless");
  }

  // The result sheet.

  showResult(content: ResultContent): void {
    this.resultContent = content;
    this.shareLabel = "over.share";
    this.applyResult(content);
    this.setScreen("end");
    this.fitFurthest();
  }

  /**
   * The furthest point shrinks to fit beside the day's map rather than run past it, to half its
   * size at most, as the app's figure does (`minimumScaleFactor(0.5)`).
   */
  private fitFurthest(): void {
    const figure = this.result.querySelector<HTMLElement>(".stat-big .figure");
    if (!figure) return;
    figure.style.fontSize = "";
    const room = figure.clientWidth;
    if (room === 0) return;
    const range = document.createRange();
    range.selectNodeContents(figure);
    const width = range.getBoundingClientRect().width;
    if (width <= room) return;
    const size = Number.parseFloat(getComputedStyle(figure).fontSize);
    figure.style.fontSize = `${Math.max(size / 2, Math.floor((size * room) / width))}px`;
  }

  private applyResult(c: ResultContent): void {
    const r = this.result;
    bind(r, "resultKicker", c.kicker);
    bind(r, "resultHeading", c.heading);
    bind(r, "resultLine", c.line);
    bind(r, "resultFurthest", c.furthest);
    bind(r, "stat1Label", c.stats[0].label);
    bind(r, "stat1", c.stats[0].value);
    bind(r, "stat2Label", c.stats[1].label);
    bind(r, "stat2", c.stats[1].value);
    const map = r.querySelector<HTMLElement>('[data-ref="resultMap"]');
    const rows = splitShare(c.shareText).map;
    if (map) map.innerHTML = miniMap(rows ? rows.split("\n") : null);
    bind(r, "resultNote", c.note);
    const members = r.querySelector<HTMLElement>('[data-ref="members"]');
    if (members) {
      members.hidden = !c.members?.length;
      members.innerHTML = (c.members ?? [])
        .map(
          (m) =>
            `<li>${icon("banner", 18)}<span class="r-member-name">${esc(m.name)}</span><span class="kicker">${esc(m.line)}</span></li>`,
        )
        .join("");
    }
    const portrait = r.querySelector<HTMLImageElement>('[data-ref="portrait"]');
    if (portrait) {
      portrait.hidden = c.portrait === null;
      if (c.portrait) {
        if (portrait.getAttribute("src") !== c.portrait.src) portrait.src = c.portrait.src;
        portrait.alt = c.portrait.alt;
      }
    }
    this.applyListRow();
  }

  /**
   * What to do next (decision 0040). Today's expedition and a practice run share first, then the
   * way on, a new world, a whole button, and today's board again, a link under it; while Add your
   * name stands under the place, it is the first action and Share a plain button. An endless run
   * sets out again first, then shares or goes to today's expedition.
   */
  private applyActions(c: ResultContent): void {
    const r = this.result;
    const [primary, second, third] = (["primary", "second", "third"] as const).map((ref) =>
      r.querySelector<HTMLButtonElement>(`[data-ref="${ref}"]`),
    );
    const set = (
      b: HTMLButtonElement | null | undefined,
      action: string,
      key: StringKey,
      kind: "primary" | "secondary" | "link",
    ) => {
      if (!b) return;
      b.dataset.action = `result:${action}`;
      b.textContent = this.t(key);
      b.className = kind;
    };
    const daily = c.kind === "daily";
    r.querySelector('[data-ref="pair"]')?.classList.toggle("stacked", daily);
    if (daily) {
      set(primary, "share", this.shareLabel, this.listRow?.add ? "secondary" : "primary");
      set(second, "endless", "over.newWorld", "secondary");
      set(third, "practice", "over.practiceBoard", "link");
    } else {
      set(primary, "again", "over.setOutAgain", "primary");
      set(second, "share", this.shareLabel, "secondary");
      set(third, "daily", "button.daily", "secondary");
    }
  }

  private onResult(what: string): void {
    if (what === "share") this.h.share();
    else if (what === "again") this.h.again();
    else if (what === "practice") this.h.practice();
    else if (what === "endless") this.h.pickMode("endless");
    else if (what === "daily") this.h.pickMode("daily");
  }

  /**
   * Today's top explorers on the result sheet of today's expedition (decisions 0039 and 0040): the
   * place in the creature's place, the link to the list, and Add your name or the name the
   * expedition is on the list as; null shows the creature, as for practice runs, endless runs and
   * with the list off.
   */
  setListPlace(row: ResultListRow | null): void {
    this.listRow = row;
    this.applyListRow();
  }

  private applyListRow(): void {
    const r = this.result;
    const top = r.querySelector<HTMLElement>('[data-ref="listTop"]');
    const head = r.querySelector<HTMLElement>('[data-ref="resultHead"]');
    if (!top || !head) return;
    const row = this.listRow;
    top.hidden = row === null;
    head.hidden = row !== null;
    // The sheet is named by the heading that shows.
    r.setAttribute("aria-labelledby", row ? "resultPlace" : "resultTitle");
    if (row) {
      bind(top, "listPlace", row.heading);
      bind(top, "listLink", row.link);
      bind(top, "listAs", row.as);
      const add = top.querySelector<HTMLElement>('[data-ref="listAdd"]');
      if (add) add.hidden = !row.add;
    }
    if (this.resultContent) this.applyActions(this.resultContent);
  }

  /** The name dialog, to add the first expedition or to change the name, `name` ready to edit. */
  openListName(mode: "add" | "rename", name: string): void {
    this.listNameMode = mode;
    const d = this.listNameDialog;
    bind(d, "listNameTitle", this.t(mode === "add" ? "list.addTitle" : "list.renameTitle"));
    bind(d, "listNameBody", this.t(mode === "add" ? "list.addBody" : "list.renameBody"));
    bind(d, "listNameSubmit", this.t(mode === "add" ? "list.add" : "clearing.renameSave"));
    bind(d, "listNameError", "");
    const input = d.querySelector<HTMLInputElement>("input");
    d.hidden = false;
    if (input) {
      input.value = name;
      input.focus();
      input.select();
    }
  }

  /** Why the name was refused, under the field; the dialog stays open. */
  listNameError(text: string): void {
    bind(this.listNameDialog, "listNameError", text);
    this.listNameDialog.querySelector<HTMLInputElement>("input")?.focus();
  }

  /** A question before something that cannot be undone; `then` runs if the player goes on. */
  private openConfirm(
    title: StringKey,
    body: StringKey,
    button: StringKey,
    then: () => void,
  ): void {
    const d = this.listConfirmDialog;
    bind(d, "listConfirmTitle", this.t(title));
    bind(d, "listConfirmBody", this.t(body));
    bind(d, "listConfirmButton", this.t(button));
    this.confirmed = then;
    d.hidden = false;
    d.querySelector<HTMLElement>('[data-action="closeDialog"]')?.focus();
  }

  /**
   * A row of today's expeditions held, or right-clicked, asks whether to report its name, as a
   * clearing held in the row opens its menu.
   */
  private wireReports(menu: HTMLElement): void {
    let down: { x: number; y: number } | null = null;
    const cancel = () => {
      if (this.reportTimer !== null) clearTimeout(this.reportTimer);
      this.reportTimer = null;
      down = null;
    };
    const ask = (id: number) =>
      this.openConfirm("list.reportTitle", "list.reportBody", "list.report", () =>
        this.h.listReport(id),
      );
    const rowOf = (e: Event) => (e.target as Element).closest<HTMLElement>("[data-report]");
    menu.addEventListener("pointerdown", (e) => {
      const row = rowOf(e);
      cancel();
      if (!row || e.button !== 0) return;
      down = { x: e.clientX, y: e.clientY };
      const id = Number(row.dataset.report);
      this.reportTimer = setTimeout(() => {
        this.reportTimer = null;
        down = null;
        ask(id);
      }, HOLD_MS);
    });
    menu.addEventListener("pointermove", (e) => {
      if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) > 8) cancel();
    });
    menu.addEventListener("pointerup", cancel);
    menu.addEventListener("pointercancel", cancel);
    menu.addEventListener("contextmenu", (e) => {
      const row = rowOf(e);
      if (!row) return;
      e.preventDefault();
      cancel();
      ask(Number(row.dataset.report));
    });
  }

  /** After a share: the button says what happened. */
  setShared(how: "shared" | "copied"): void {
    this.shareLabel = how === "shared" ? "over.shared" : "over.copied";
    if (this.resultContent) this.applyResult(this.resultContent);
  }

  /** The height the result sheet covers, in CSS pixels, so the camera can frame above it. */
  get resultHeight(): number {
    return this.result.getBoundingClientRect().height;
  }

  // The menu.

  /** Open, and not on its way out. */
  get menuOpen(): boolean {
    return !this.menu.hidden && this.menu.classList.contains("open");
  }

  /** Opens the menu on a page; `animate` false shows it at once, as after a change of language. */
  openMenu(page: MenuPage = "home", from: HTMLElement | null = null, animate = true): void {
    clearTimeout(this.menuTimer);
    if (!animate) this.menu.classList.add("open");
    if (!this.menuOpen) this.menuReturn = from ?? (document.activeElement as HTMLElement | null);
    const shown = !this.menu.hidden;
    this.menu.hidden = false;
    this.layer.classList.add("menu-open");
    this.h.menuChanged(true);
    this.showPage(page, animate);
    // A frame in the closed pose first, so the menu slides in and the lines turn into a cross.
    if (!shown) void this.menu.offsetWidth;
    this.menu.classList.add("open");
  }

  closeMenu(): void {
    if (!this.menuOpen) return;
    this.menu.classList.remove("open");
    this.layer.classList.remove("menu-open");
    this.h.menuChanged(false);
    this.menuReturn?.focus?.({ preventScroll: true });
    this.menuReturn = null;
    const hide = () => {
      this.menu.hidden = true;
    };
    if (this.layer.dataset.motion === "reduce") hide();
    else this.menuTimer = setTimeout(hide, MENU_OUT_MS);
  }

  private showPage(page: MenuPage, animate = true): void {
    this.page = page;
    if (page === "list") this.h.listShown();
    this.renderPage();
    const body = this.menu.querySelector<HTMLElement>('[data-ref="menuBody"]');
    if (body) {
      body.scrollTop = 0;
      if (animate) this.cascade(body);
    }
    const focus =
      page === "home"
        ? this.menu.querySelector<HTMLElement>(".close")
        : this.menu.querySelector<HTMLElement>(".back");
    focus?.focus({ preventScroll: true });
  }

  /** Fades a new page in row by row; a page drawn again from fresh data does not cascade. */
  private cascade(body: HTMLElement): void {
    const rows = body.querySelectorAll<HTMLElement>(
      ":scope > :not(.menu-rows, .out-rows), .menu-rows > *, .out-rows > *",
    );
    for (const [i, row] of rows.entries()) row.style.setProperty("--i", String(Math.min(i, 12)));
    this.menu.classList.remove("cascade");
    void body.offsetWidth;
    this.menu.classList.add("cascade");
    clearTimeout(this.cascadeTimer);
    this.cascadeTimer = setTimeout(() => this.menu.classList.remove("cascade"), CASCADE_MS);
  }

  /** Draws the open page again from fresh data, keeping its scroll. */
  renderPage(): void {
    if (this.menu.hidden) return;
    const body = this.menu.querySelector<HTMLElement>('[data-ref="menuBody"]');
    if (!body) return;
    const scroll = body.scrollTop;
    const data = this.h.menuData();
    this.menu.dataset.page = this.page;
    bind(this.menu, "menuTitle", pageTitle(this.t, this.page));
    // A title too wide for the bar, as some languages write About, gets smaller and may take two
    // lines before it is cut short.
    const title = this.menu.querySelector<HTMLElement>("#menuTitle");
    if (title) {
      title.classList.remove("long");
      if (title.scrollWidth > title.clientWidth) title.classList.add("long");
    }
    body.innerHTML = pageHtml(this.t, this.page, data);
    body.scrollTop = scroll;
  }

  // Dialogs.

  showSealed(title: string, body: string, nextLabel: string): void {
    bind(this.sealed, "sealedTitle", title);
    bind(this.sealed, "sealedBody", body);
    bind(this.sealed, "sealedNext", nextLabel);
    this.sealed.hidden = false;
    this.sealed.querySelector<HTMLElement>('[data-action="startNext"]')?.focus();
  }

  closeDialogs(): void {
    this.sealed.hidden = true;
    this.renameDialog.hidden = true;
    this.deleteDialog.hidden = true;
    this.listNameDialog.hidden = true;
    this.listConfirmDialog.hidden = true;
    this.confirmed = null;
  }

  private get anyDialog(): boolean {
    return [
      this.sealed,
      this.renameDialog,
      this.deleteDialog,
      this.listNameDialog,
      this.listConfirmDialog,
    ].some((d) => !d.hidden);
  }

  /** Escape: the menu first, then a dialog, then a held clearing's menu. True when something closed. */
  escape(): boolean {
    if (this.menuOpen) {
      this.closeMenu();
      return true;
    }
    if (this.anyDialog) {
      this.closeDialogs();
      return true;
    }
    if (!this.clearingsMenu.hidden) {
      this.closeClearingMenu();
      return true;
    }
    return false;
  }

  /** True while the interface takes the keyboard from the world. */
  get dialogOpen(): boolean {
    return this.menuOpen || this.anyDialog || this.screen !== "play";
  }

  setDebug(text: string | null): void {
    this.debug.hidden = text === null;
    if (text !== null) this.debug.textContent = text;
  }

  /** The gallery and portraits show the world only. */
  hideAll(): void {
    this.layer.classList.add("world-only");
  }

  /** A short line where the hint shows, in the gallery: the model's name. */
  showLabel(text: string): void {
    this.hint.hidden = false;
    this.hint.textContent = text;
  }

  /** Reduced motion from the setting: the result sheet appears without sliding. */
  setReducedMotion(on: boolean): void {
    if (on) this.layer.dataset.motion = "reduce";
    else delete this.layer.dataset.motion;
  }
}

/**
 * Applies a theme to the interface layer: the three roles as CSS variables plus the colour scheme,
 * the interface's own background and text where the theme has them (decision 0037).
 */
export function applyTheme(theme: ThemeDef): void {
  const style = document.documentElement.style;
  style.setProperty("--paper", theme.background ?? theme.paper);
  style.setProperty("--ink", theme.text ?? theme.ink);
  style.setProperty("--accent", theme.accent);
  style.setProperty("color-scheme", theme.appearance);
  document.documentElement.dataset.theme = theme.id;
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.append(meta);
  }
  meta.content = theme.background ?? theme.paper;
}
